import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface TranscriptSuccessEvent {
  data: any;
  source: string;
}

@Injectable({
  providedIn: 'root',
})
export class CreateChunkService {
  private insertBelowSource = new Subject<any>();
  insertBelow$ = this.insertBelowSource.asObservable();

  private chunkState = new BehaviorSubject<any>(null);
  chunk$ = this.chunkState.asObservable();

  // ++ Add a BehaviorSubject to track if we are in create/edit mode.
  private createModeState = new BehaviorSubject<boolean>(false);
  createMode$ = this.createModeState.asObservable();

  // ++ Add a Subject to trigger the submit action in CreateChunkComponent.
  private submitChunkSource = new Subject<void>();
  submitChunk$ = this.submitChunkSource.asObservable();

  // ++ Add a Subject to trigger the text type change.
  private textTypeChangeSource = new Subject<string>();
  textTypeChange$ = this.textTypeChangeSource.asObservable();

  private editorFocusState = new BehaviorSubject<boolean>(false);
  editorFocus$ = this.editorFocusState.asObservable();

  // State for modify mode
  public canUndoState = new BehaviorSubject<boolean>(false);
  canUndo$ = this.canUndoState.asObservable();

  public canRedoState = new BehaviorSubject<boolean>(false);
  canRedo$ = this.canRedoState.asObservable();

  public creatingChunkState = new BehaviorSubject<boolean>(false);
  creatingChunk$ = this.creatingChunkState.asObservable();

  public savingChunkState = new BehaviorSubject<boolean>(false);
  savingChunk$ = this.savingChunkState.asObservable();

  public saveSuccessState = new BehaviorSubject<boolean>(false);
  saveSuccess$ = this.saveSuccessState.asObservable();

  // Events for modify mode
  private undoSource = new Subject<void>();
  undo$ = this.undoSource.asObservable();

  private redoSource = new Subject<void>();
  redo$ = this.redoSource.asObservable();

  private copySource = new Subject<void>();
  copy$ = this.copySource.asObservable();

  private textGeneratedSource = new Subject<string>();
  textGenerated$ = this.textGeneratedSource.asObservable();

  private uploadCompleteSource = new Subject<void>();
  uploadComplete$ = this.uploadCompleteSource.asObservable();

  private recordingSavedSource = new Subject<{ file: Blob; type: 'video' | 'image' }>();
  recordingSaved$ = this.recordingSavedSource.asObservable();

  insertBelow(status) {
    this.insertBelowSource.next(status);
  }

  updateChunk(chunk: any) {
    this.chunkState.next(chunk);
  }

  // ++ Method to set the current create/edit mode.
  setCreateMode(isCreating: boolean) {
    this.createModeState.next(isCreating);
  }

  // ++ Method to trigger the chunk submission.
  submitChunk() {
    this.submitChunkSource.next();
  }

  // ++ Method to change the text type.
  changeTextType(type: string) {
    this.textTypeChangeSource.next(type);
  }

  setEditorFocus(isFocused: boolean) {
    this.editorFocusState.next(isFocused);
  }

  // Methods to update state
  setCanUndo(canUndo: boolean) {
    this.canUndoState.next(canUndo);
  }

  setCanRedo(canRedo: boolean) {
    this.canRedoState.next(canRedo);
  }

  setCreatingChunk(creatingChunk: boolean) {
    this.creatingChunkState.next(creatingChunk);
  }

  setSavingChunk(savingChunk: boolean) {
    this.savingChunkState.next(savingChunk);
  }

  setSaveSuccess(saveSuccess: boolean) {
    this.saveSuccessState.next(saveSuccess);
  }

  // Methods to trigger events
  undo() {
    this.undoSource.next();
  }

  redo() {
    this.redoSource.next();
  }

  copy() {
    this.copySource.next();
  }

  textGenerated(text: string) {
    this.textGeneratedSource.next(text);
  }

  uploadComplete() {
    this.uploadCompleteSource.next();
  }

  recordingSaved(data: { file: Blob; type: 'video' | 'image' }) {
    this.recordingSavedSource.next(data);
  }
}
